import {
  phaseOfTask,
  type GettingStartedPhase,
  type GettingStartedTaskKey,
} from "@/constants/gettingStarted";
import { tracking } from "@/utils/tracking";

/**
 * Getting Started analytics — every event name is built here, so the full
 * list reads in one place and each name says exactly what happened:
 *
 *   getting_started_viewed                       first view in a session
 *   getting_started_welcome_get_started_clicked  / _look_around_clicked
 *   getting_started_<phase>_phase_viewed         moved to a phase (activate…)
 *   getting_started_<phase>_phase_completed      e.g. activate_phase_completed
 *   getting_started_completed                    whole journey done
 *   getting_started_<step>_step_opened           clicked a step open
 *   getting_started_<cta>_clicked                the step's button, e.g.
 *                                                complete_profile_clicked
 *   getting_started_<step>_step_completed        ticked / marked / approved
 *   getting_started_<step>_step_undone / _step_skipped
 *
 * Step-specific actions:
 *   Code of Conduct   code_of_conduct_review_clicked, code_of_conduct_signed,
 *                     explainer_pdf_opened, explainer_marked_read,
 *                     archetypes_pdf_opened, archetypes_marked_read
 *   Program Coach     program_coach_star_selected, program_coach_prompt_generated,
 *                     program_coach_prompt_regenerated, program_coach_prompt_failed,
 *                     program_coach_prompt_copied
 *   S.T.A.R.s         add_star_clicked (empty state / coach picker)
 *
 * Deliberately NOT tracked: dock open/clicks, prompt impressions, "Not yet",
 * deep-link auto-expands — navigation noise. A step completed from the dock's
 * Done prompt is still <step>_step_completed, with method "from_prompt".
 */

/** Readable name per step, and the event its button fires. */
const STEP_ANALYTICS: Record<GettingStartedTaskKey, { step: string; cta: string | null }> = {
  "xo.activate.profile": { step: "profile", cta: "complete_profile" },
  "xo.activate.conduct": { step: "code_of_conduct", cta: null },
  "xo.activate.linkedin": { step: "linkedin", cta: "update_linkedin" },
  "xo.activate.stars": { step: "stars", cta: "add_star" },
  "xo.activate.calibration": { step: "calibration", cta: "book_calibration" },
  "xo.activate.kickoff": { step: "kickoff", cta: "pick_kickoff_session" },
  "xo.publish.live": { step: "public_profile", cta: "review_public_profile" },
  "xo.publish.carousel": { step: "spotlight_carousel", cta: "review_carousel" },
  "xo.publish.announce": { step: "announcement", cta: "post_announcement" },
  "xo.connect.peer": { step: "peer_intro", cta: "book_peer_call" },
  "xo.connect.refer-achievers": { step: "refer_achievers", cta: "refer_achiever" },
  "xo.connect.refer-xos": { step: "refer_xos", cta: "refer_xo" },
  "xo.launch.design": { step: "program_design", cta: "create_first_program" },
};

type Props = Record<string, string | number | boolean | null | undefined>;

/** Sends `getting_started_<event>`. */
export const trackGettingStarted = (event: string, props?: Props) =>
  tracking.gettingStarted(event, props);

const phaseName = (phase: GettingStartedPhase) => phase.name.toLowerCase();

/** Context on every step event, so a chart can still group by phase/order. */
const stepProps = (key: GettingStartedTaskKey): Props => {
  const phase = phaseOfTask(key);
  return {
    task_key: key,
    phase: phaseName(phase),
    step_index: phase.tasks.findIndex((task) => task.key === key) + 1,
  };
};

export const trackStep = (
  key: GettingStartedTaskKey,
  action: "step_opened" | "step_completed" | "step_undone" | "step_skipped",
  extra?: Props,
) => trackGettingStarted(`${STEP_ANALYTICS[key].step}_${action}`, { ...stepProps(key), ...extra });

/** The step's main button — e.g. getting_started_complete_profile_clicked. */
export const trackStepCta = (key: GettingStartedTaskKey) => {
  const { cta } = STEP_ANALYTICS[key];
  if (cta) trackGettingStarted(`${cta}_clicked`, stepProps(key));
};

export const trackPhase = (
  phase: GettingStartedPhase,
  action: "phase_viewed" | "phase_completed",
) => trackGettingStarted(`${phaseName(phase)}_${action}`, { phase_id: phase.id });

/**
 * getting_started_viewed, once per browser session per user — not again on
 * every return to the page while moving between steps. Storage failures
 * (private windows) just mean it may fire once more; never an error.
 */
export const trackViewedOncePerSession = (userId: string, props: Props) => {
  const key = `fa_gs_viewed_v1:${userId}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch {
    /* storage unavailable — fall through and track */
  }
  trackGettingStarted("viewed", props);
};
